// RefundPolicy — Feb 28 2026 v6 (Randy pt-billing-compliance)
//
// Public, unauthenticated. Linked from:
//   • AutoRenewDisclosure (every paid checkout)
//   • Signup form (TOS line)
//   • Footer (once added)
//
// Kept intentionally plain and readable — dispute defensibility comes from
// clarity, not legalese. If you want to update refund terms, edit here and
// keep the language customer-friendly.
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { SUPPORT_EMAIL } from "@/lib/contact";

export default function RefundPolicy() {
  useEffect(() => {
    document.title = "Refund Policy · My Silo";
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFCF8] text-[#1C1B1A]" data-testid="refund-policy-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-[#6B6A68] hover:text-[#4A5D4E] mb-8"
          data-testid="refund-policy-back-link"
        >
          <ArrowLeft size={14} /> Home
        </Link>

        <h1 className="font-serif-display text-4xl sm:text-5xl mb-3">Refund Policy</h1>
        <p className="text-sm text-[#6B6A68] mb-10">Last updated: October 16, 2026</p>

        <div className="space-y-8 text-[#3A3A38] leading-relaxed">
          <section>
            <h2 className="font-serif-display text-2xl mb-3">The short version</h2>
            <p>
              <strong>30-day money-back guarantee.</strong> Email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="underline hover:text-[#4A5D4E]">
                {SUPPORT_EMAIL}
              </a>{" "}
              within 30 days of any charge and we'll refund it — no questions asked.
              After 30 days we'll prorate to unused whole months. That's it.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-3">What's covered</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Creator subscription</strong> (My Silo platform monthly fee)
              </li>
              <li>
                <strong>Silo fan subscriptions</strong> (paid access to a specific
                creator's silo)
              </li>
              <li>
                <strong>Creator Pro add-ons</strong> (one-time purchases like AI
                Assistant, Boost, Silo Wars)
              </li>
              <li>
                <strong>Done-For-You (DFY) services</strong> — refundable if we
                haven't started the work yet; contact us to discuss otherwise
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-3">How to request a refund</h2>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                Email{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="underline hover:text-[#4A5D4E]">
                  {SUPPORT_EMAIL}
                </a>{" "}
                from the address on your account.
              </li>
              <li>Include the last-4 of the card charged (helps us find the payment fast).</li>
              <li>
                Refunds are processed within 3-5 business days. Your bank may take
                another 5-10 business days to post the credit to your statement.
              </li>
            </ol>
          </section>

          <section data-testid="refund-policy-trial" className="space-y-3">
            <h2 className="font-serif-display text-2xl mb-3">The 14-day trial</h2>
            <p>
              <strong>Cancel before day 15 and there is nothing to refund</strong> — your card is
              never charged during the trial. Cancellation is one click from{" "}
              <strong>Account → Billing</strong>, and you keep trial access through its final day
              rather than losing it the moment you cancel.
            </p>
            <p>
              <strong>Pro tools you chose to keep.</strong> Those are separate one-time charges, made
              only after you confirm each one. They are covered by the 30-day guarantee above like
              any other purchase. If you cancel the trial, every selection is voided and nothing is
              charged for them.
            </p>
            <p>
              <strong>Trial AI credit.</strong> The small Gemini credit we fund at our own cost is
              promotional. It has no cash value, cannot be withdrawn, and expires when the trial ends
              or converts — so there is nothing to refund there either.
            </p>
          </section>

          <section data-testid="refund-policy-resting" className="space-y-3">
            <h2 className="font-serif-display text-2xl mb-3">If a creator&apos;s silo goes quiet</h2>
            <p>
              When a creator&apos;s own subscription ends, we <strong>pause collection</strong> on
              your subscription to their silo instead of cancelling it. You are{" "}
              <strong>not billed for a month in which the silo was resting</strong>, you keep your
              price and your place, and nothing is deleted.
            </p>
            <p>
              <strong>One honest limitation.</strong> Generating answers costs money, funded by the
              creator&apos;s own AI key or by credit on the silo. If a resting silo has neither, no
              answers can be generated for anyone — including a subscriber with paid-through time
              left. Paid-through time guarantees no further billing and no loss of access; it cannot
              guarantee an answer. <strong>We don&apos;t automatically credit or refund those
              remaining days</strong> — but the 30-day guarantee at the top of this page still
              applies, so email us and we&apos;ll sort it out.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-3">Auto-renewals</h2>
            <p>
              Every paid subscription on My Silo auto-renews unless you cancel.
              Cancellation is one click from{" "}
              <strong>Account → Billing</strong> — no fees, no phone calls, no
              retention offers. Once canceled, you keep your access through the end
              of the current billing period. No further charges after that.
            </p>
          </section>

          <section data-testid="refund-policy-voucher-credit">
            <h2 className="font-serif-display text-2xl mb-3">Prepaid AI credit (voucher credit)</h2>
            <p>
              <strong>What we refund.</strong> Unused voucher credit, in full, back to the
              original payment method. There&apos;s no time limit and no fee.
            </p>
            <p>
              <strong>What we don&apos;t refund.</strong> Credit already spent on AI usage, and
              credit already withdrawn as cash. In both cases the money has left our side —
              the AI providers billed us, or we paid you out.
            </p>
            <p>
              <strong>Partial refunds.</strong> If you&apos;ve used some of a top-up, we refund
              the unused remainder. You don&apos;t lose the rest of your balance.
            </p>
            <p>
              <strong>How to ask.</strong> Contact support with the date and amount of the
              top-up. We&apos;ll show you exactly what&apos;s refundable before anything is
              charged back.
            </p>
            <p>
              <strong>Promotional credit.</strong> Credit we gave you for free isn&apos;t
              refundable, since nothing was paid for it. It may be withdrawn if it was granted
              in error.
            </p>
            <p>
              <strong>Disputes.</strong> If you ask your bank to reverse a top-up instead of
              contacting us, the credit from that payment is held while the dispute is open and
              can&apos;t be spent or withdrawn. Talking to us first is faster and doesn&apos;t
              freeze anything.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-3">Before you dispute a charge</h2>
            <p>
              Please contact us first. Bank chargebacks cost both sides money and
              time and rarely resolve faster than a direct email. If you email us
              first at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="underline hover:text-[#4A5D4E]">
                {SUPPORT_EMAIL}
              </a>
              , we'll issue the refund and skip the bank entirely. Fair to
              everyone.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-3">Fraud + abuse</h2>
            <p>
              This policy is generous because we trust our members. Accounts
              found engaging in refund fraud, chargeback fraud, or artificial
              referral schemes forfeit their refund eligibility and may be
              terminated. We reserve the right to decline refunds after
              multiple abusive requests from the same user or payment method.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-3">Questions</h2>
            <p>
              Email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="underline hover:text-[#4A5D4E]">
                {SUPPORT_EMAIL}
              </a>
              . We aim to respond within one business day.
            </p>
          </section>

          <div className="pt-8 border-t border-[#E4E2DC] text-sm text-[#6B6A68]">
            See also:{" "}
            <Link to="/terms" className="underline hover:text-[#4A5D4E]" data-testid="refund-policy-terms-link">
              Terms of Service
            </Link>
            {" · "}
            <Link to="/privacy" className="underline hover:text-[#4A5D4E]" data-testid="refund-policy-privacy-link">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
